import React, { useState, useEffect } from 'react';
import { Table, Button, Tag, message, Modal, Form, Input, InputNumber, Switch, Upload, Space, Typography, Popconfirm } from 'antd';
import { PlusOutlined, UploadOutlined } from '@ant-design/icons';
import { pointProductApi, uploadApi } from '../../services/api';

const { Title } = Typography;
const { TextArea } = Input;

// 通用图片上传（COS）
const ImageUploader = ({ value = {}, onChange }) => {
  const [uploading, setUploading] = useState(false);

  const customRequest = async ({ file, onSuccess, onError }) => {
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('image', file);
      const res = await uploadApi.uploadImage(formData);
      onChange({ url: res.data.url });
      message.success('图片上传成功');
      onSuccess(res);
    } catch (err) {
      message.error(err.message || '图片上传失败');
      onError(err);
    } finally {
      setUploading(false);
    }
  };

  return (
    <Upload accept="image/jpeg,image/png,image/webp" listType="picture-card" showUploadList={false} customRequest={customRequest}>
      {value?.url ? (
        <img src={value.url} alt="商品图" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
      ) : (
        <div>
          <UploadOutlined />
          <div style={{ marginTop: 8 }}>{uploading ? '上传中...' : '上传图片'}</div>
        </div>
      )}
    </Upload>
  );
};

const PointProducts = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null); // null=新建
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await pointProductApi.listAll();
      setData(res.data || []);
    } catch (err) {
      message.error(err.message || '加载失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ status: true, stock: -1, sortOrder: 0, pointsCost: 100 });
    setModalOpen(true);
  };

  const openEdit = (record) => {
    setEditing(record);
    form.setFieldsValue({
      name: record.name,
      description: record.description,
      image: record.image_url ? { url: record.image_url } : {},
      pointsCost: record.points_cost,
      stock: record.stock,
      status: record.status === 1,
      sortOrder: record.sort_order,
    });
    setModalOpen(true);
  };

  const handleSubmit = async () => {
    let values;
    try { values = await form.validateFields(); } catch { return; }
    setSaving(true);
    try {
      const payload = {
        name: values.name,
        description: values.description || undefined,
        imageUrl: values.image?.url || undefined,
        pointsCost: values.pointsCost,
        stock: values.stock,
        status: values.status ? 1 : 0,
        sortOrder: values.sortOrder,
      };
      if (editing) {
        await pointProductApi.update(editing.id, payload);
      } else {
        await pointProductApi.create(payload);
      }
      message.success(editing ? '更新成功' : '创建成功');
      setModalOpen(false);
      fetchData();
    } catch (err) {
      message.error(err.message || '保存失败');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await pointProductApi.remove(id);
      message.success('删除成功');
      fetchData();
    } catch (err) {
      message.error(err.message || '删除失败');
    }
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', width: 60 },
    {
      title: '图片', dataIndex: 'image_url', width: 80,
      render: (v) => v ? <img src={v} alt="" style={{ width: 48, height: 48, objectFit: 'contain' }} /> : '-'
    },
    { title: '商品名称', dataIndex: 'name' },
    { title: '所需积分', dataIndex: 'points_cost', width: 100, render: (v) => <Tag color="orange">{v} 积分</Tag> },
    {
      title: '库存', dataIndex: 'stock', width: 90,
      render: (v) => v === -1 ? '不限' : (v > 0 ? <Tag color="green">{v}</Tag> : <Tag color="red">无货</Tag>)
    },
    { title: '排序', dataIndex: 'sort_order', width: 70 },
    {
      title: '状态', dataIndex: 'status', width: 80,
      render: (v) => v === 1 ? <Tag color="green">上架</Tag> : <Tag>下架</Tag>
    },
    {
      title: '操作', width: 140, render: (_, r) => (
        <Space>
          <Button type="link" size="small" onClick={() => openEdit(r)}>编辑</Button>
          <Popconfirm title="确定删除该商品？" onConfirm={() => handleDelete(r.id)}>
            <Button type="link" size="small" danger>删除</Button>
          </Popconfirm>
        </Space>
      )
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <Title level={4} style={{ margin: 0 }}>积分商品</Title>
        <Button type="primary" icon={<PlusOutlined />} style={{ background: '#8B7355' }} onClick={openCreate}>
          新增商品
        </Button>
      </div>

      <Table columns={columns} dataSource={data} rowKey="id" loading={loading}
        pagination={{ showSizeChanger: false, showTotal: (t) => `共 ${t} 条` }} />

      <Modal
        title={editing ? '编辑商品' : '新增商品'}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={handleSubmit}
        confirmLoading={saving}
        okText="保存"
        cancelText="取消"
        okButtonProps={{ style: { background: '#8B7355' } }}
      >
        <Form form={form} layout="vertical">
          <Form.Item name="name" label="商品名称" rules={[{ required: true, message: '请输入商品名称' }, { max: 100 }]}>
            <Input placeholder="如 羊脂白玉平安扣" maxLength={100} />
          </Form.Item>
          <Form.Item name="image" label="商品图片">
            <ImageUploader />
          </Form.Item>
          <Form.Item name="description" label="商品描述" rules={[{ max: 500 }]}>
            <TextArea rows={3} placeholder="商品描述（选填）" maxLength={500} />
          </Form.Item>
          <Form.Item name="pointsCost" label="所需积分" rules={[{ required: true, message: '请输入所需积分' }]}>
            <InputNumber min={1} style={{ width: '100%' }} placeholder="如 500" />
          </Form.Item>
          <Form.Item name="stock" label="库存" extra="-1 表示不限库存" rules={[{ required: true }]}>
            <InputNumber min={-1} style={{ width: '100%' }} placeholder="-1 为不限" />
          </Form.Item>
          <Form.Item name="sortOrder" label="排序" extra="越小越靠前">
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="status" label="上架" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default PointProducts;
